// Pay type, fixed salary and weekly working schedule of a staff member. Shared by the
// browser and the server, so both validate and count a schedule the same way.
// Schedule: { regular: Day[7], summer: { from: 'MM-DD', to: 'MM-DD', days: Day[7] } | null }
// Day: up to two [start, end] 'HH:MM' ranges (split shift), Monday first.
(function (root) {
    const DAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
    const PAY_TYPES = ['hourly', 'fixed'];
    const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
    const MONTH_DAY = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
    const minutes = time => Number(time.slice(0, 2)) * 60 + Number(time.slice(3));
    const pad = number => String(number).padStart(2, '0');

    function cleanDays(days) {
        if (!Array.isArray(days) || days.length !== 7) return null;
        const result = [];
        for (const day of days) {
            if (!Array.isArray(day) || day.length > 2) return null;
            const ranges = [];
            for (const range of day) {
                if (!Array.isArray(range) || range.length !== 2 || !range.every(t => typeof t === 'string' && TIME.test(t)) ||
                    minutes(range[0]) >= minutes(range[1])) return null;
                ranges.push([range[0], range[1]]);
            }
            ranges.sort((a, b) => minutes(a[0]) - minutes(b[0]));
            if (ranges.length === 2 && minutes(ranges[0][1]) > minutes(ranges[1][0])) return null;
            result.push(ranges);
        }
        return result;
    }
    // 2024 is a leap year, so 29 February is a valid yearly date.
    const validMonthDay = value => typeof value === 'string' && MONTH_DAY.test(value) &&
        Number(value.slice(3)) <= new Date(2024, Number(value.slice(0, 2)), 0).getDate();

    /** The normalized schedule, null for "no schedule", or undefined when it is not valid. */
    function normalizeSchedule(raw) {
        if (raw === null) return null;
        if (!raw || typeof raw !== 'object' || Array.isArray(raw) || Object.keys(raw).some(k => !['regular', 'summer'].includes(k))) return undefined;
        const regular = cleanDays(raw.regular);
        if (!regular) return undefined;
        let summer = null;
        if (raw.summer != null) {
            const s = raw.summer;
            if (typeof s !== 'object' || Array.isArray(s) || Object.keys(s).some(k => !['from', 'to', 'days'].includes(k)) ||
                !validMonthDay(s.from) || !validMonthDay(s.to)) return undefined;
            const days = cleanDays(s.days);
            if (!days) return undefined;
            summer = { from: s.from, to: s.to, days };
        }
        return { regular, summer };
    }
    function isSummer(schedule, date) {
        const summer = schedule?.summer;
        if (!summer) return false;
        const day = pad(date.getMonth() + 1) + '-' + pad(date.getDate());
        // A range such as 12-15 → 01-15 crosses New Year.
        return summer.from <= summer.to ? day >= summer.from && day <= summer.to : day >= summer.from || day <= summer.to;
    }
    const daysFor = (schedule, date = new Date()) => schedule?.regular ? (isSummer(schedule, date) ? schedule.summer.days : schedule.regular) : null;
    const dayHours = day => (day || []).reduce((total, [start, end]) => total + (minutes(end) - minutes(start)) / 60, 0);
    const weeklyHours = (schedule, date = new Date()) => (daysFor(schedule, date) || []).reduce((total, day) => total + dayHours(day), 0);
    // The usual week, outside the summer schedule (what the contract states).
    const regularWeeklyHours = schedule => (schedule?.regular || []).reduce((total, day) => total + dayHours(day), 0);

    const isFixed = person => person?.payType === 'fixed';

    // ─── Contracts: a person may be hired several times (each with its dates and pay) ───
    const day10 = value => value ? String(value).slice(0, 10) : null;
    /**
     * A person's contracts, oldest first: { id, startDate, endDate (null = open), hourlyRate,
     * monthlySalary, advisoryNotified }. Records from before contracts existed count their
     * hire and termination dates as one contract.
     */
    function contractsOf(person) {
        const list = Array.isArray(person?.contracts) && person.contracts.length ? person.contracts
            : person?.hireDate ? [{ startDate: person.hireDate, endDate: person.terminationDate, hourlyRate: person.hourlyRate,
                monthlySalary: person.monthlySalary, advisoryNotified: !!person.advisoryNotificationChecked, advisoryNotifiedAt: person.advisoryNotificationDate }] : [];
        return list.map(c => ({ ...c, startDate: day10(c.startDate), endDate: day10(c.endDate) }))
            .filter(c => c.startDate).sort((a, b) => a.startDate.localeCompare(b.startDate));
    }
    const covers = (c, iso) => c.startDate <= iso && (!c.endDate || c.endDate >= iso);
    /** The contract in force on a day (YYYY-MM-DD), or null. */
    const contractOn = (person, iso) => contractsOf(person).find(c => covers(c, iso)) || null;
    const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
    /** The contract that describes the person today: the one in force, else the next one, else the last one. */
    function focusContract(person, today = todayIso()) {
        const list = contractsOf(person);
        return list.find(c => covers(c, today)) || list.find(c => c.startDate > today) || list[list.length - 1] || null;
    }
    /**
     * The person with hire/termination dates, pay and advisory state taken from their focus
     * contract, so whatever reads a single contract keeps working.
     */
    function withFocus(person, today = todayIso()) {
        if (!person || !Array.isArray(person.contracts) || !person.contracts.length) return person;
        const c = focusContract(person, today);
        return { ...person, hireDate: c.startDate, terminationDate: c.endDate || null,
            ...(c.hourlyRate != null ? { hourlyRate: c.hourlyRate } : {}), ...(c.monthlySalary != null ? { monthlySalary: c.monthlySalary } : {}),
            advisoryNotificationChecked: !!c.advisoryNotified, advisoryNotificationDate: c.advisoryNotifiedAt || null };
    }
    /** Whether a person had a contract on that day. No contract at all: employed (nothing to go by). */
    const employedOn = (person, iso) => { const list = contractsOf(person); return !list.length || list.some(c => covers(c, iso)); };
    /** Days of that month (monthIndex 0-11) under some contract, and the month's length. */
    function contractDaysInMonth(person, year, monthIndex) {
        const length = new Date(year, monthIndex + 1, 0).getDate();
        const list = contractsOf(person);
        if (!list.length) return { days: length, length, contracts: [] };
        const first = `${year}-${pad(monthIndex + 1)}-01`, last = `${year}-${pad(monthIndex + 1)}-${pad(length)}`;
        const inMonth = list.filter(c => c.startDate <= last && (!c.endDate || c.endDate >= first));
        let days = 0;
        for (const c of inMonth) {
            const from = c.startDate > first ? Number(c.startDate.slice(8)) : 1;
            const to = c.endDate && c.endDate < last ? Number(c.endDate.slice(8)) : length;
            days += to - from + 1;
        }
        return { days, length, contracts: inMonth };
    }
    // Freelance (autónomo) periods: invoices instead of payslips and no hire notice to the advisory.
    /** Whether the person is freelance today (their focus contract). */
    const isFreelance = (person, today = todayIso()) => !!focusContract(person, today)?.freelance;
    /** Whether that month (monthIndex 0-11) is invoiced: its last contract that month is freelance. */
    const freelanceInMonth = (person, year, monthIndex) => !!contractDaysInMonth(person, year, monthIndex).contracts.at(-1)?.freelance;
    /** Whether some contract covers any day of that month (monthIndex 0-11). No contract: employed. */
    const employedInMonth = (person, year, monthIndex) => contractDaysInMonth(person, year, monthIndex).days > 0;
    // A fixed salary is never also paid per hour: its classes cost nothing extra.
    const hourlyRate = person => isFixed(person) ? 0 : Number(person?.hourlyRate) || 0;
    const monthlySalary = person => isFixed(person) ? Number(person?.monthlySalary) || 0 : 0;
    /** The hourly rate on a day: that of the contract in force, else the person's current one. */
    function rateOn(person, iso) {
        if (isFixed(person)) return 0;
        const c = contractOn(person, iso);
        return c && c.hourlyRate != null ? Number(c.hourlyRate) || 0 : hourlyRate(person);
    }
    /**
     * Net cost of a fixed salary in a month with a contract: the salary of the (last) contract
     * that month. Unpaid leave takes away its share of the month (unpaidDays of its natural days).
     */
    function fixedCostInMonth(person, year, monthIndex, unpaidDays = 0) {
        if (!isFixed(person)) return 0;
        const { days, length, contracts } = contractDaysInMonth(person, year, monthIndex);
        if (!days) return 0;
        const last = contracts[contracts.length - 1];
        const salary = last && last.monthlySalary != null ? Number(last.monthlySalary) || 0 : monthlySalary(person);
        return salary * Math.max(0, length - Math.min(unpaidDays, length)) / length;
    }
    /**
     * A list of contracts as typed: dates valid, start before end, at most one open contract and
     * no two overlapping. Returns an error message, or null when it is fine.
     */
    function contractsProblem(contracts) {
        const list = [...contracts].sort((a, b) => a.startDate.localeCompare(b.startDate));
        for (const c of list) {
            if (!validDate(c.startDate) || (c.endDate && !validDate(c.endDate))) return 'Revisa las fechas del contrato.';
            if (c.endDate && c.endDate < c.startDate) return 'La baja no puede ser anterior al alta.';
        }
        for (let i = 1; i < list.length; i++) {
            const prev = list[i - 1];
            if (!prev.endDate || prev.endDate >= list[i].startDate) return 'Los contratos no pueden solaparse: el anterior debe terminar antes del siguiente alta.';
        }
        return null;
    }

    // ─── Vacations and unpaid leave (Jornada, fase 3) ───
    const DATE = /^\d{4}-\d{2}-\d{2}$/;
    const LEAVE_KINDS = ['vacation', 'unpaid'];
    const DEFAULT_VACATION_DAYS = 30;
    const CARRY_UNTIL = '01-15';
    const toIso = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const validDate = value => typeof value === 'string' && DATE.test(value) && toIso(new Date(`${value}T12:00:00`)) === value;
    /** Every natural day from `from` to `to`, both included (YYYY-MM-DD). */
    function leaveDates(from, to) {
        if (!validDate(from) || !validDate(to) || from > to) return [];
        const dates = [];
        for (let d = new Date(`${from}T12:00:00`); toIso(d) <= to; d.setDate(d.getDate() + 1)) dates.push(toIso(d));
        return dates;
    }
    const counts = leave => ['approved', 'pending'].includes(leave?.status);
    const CONTRACT_TYPES = [null, 'discontinuous'];
    /** Vacations live in SICAF only for fixed-salary staff on a regular (not discontinuous) contract. */
    const hasVacations = person => isFixed(person) && person?.contractType !== 'discontinuous';
    const vacationAllowance = person => Number.isInteger(person?.vacationDays) ? person.vacationDays : DEFAULT_VACATION_DAYS;

    /**
     * Vacation days left, per calendar year. Days are taken in date order; a day from 1 to
     * 15 January uses what was left of the previous year first. Pending requests count, so
     * nobody can ask twice for the same days. Returns { [year]: daysLeft } (may be negative).
     */
    function vacationLeft(person, leaves) {
        const allowance = vacationAllowance(person), left = {};
        const of = year => (left[year] ??= allowance);
        const days = (leaves || []).filter(l => counts(l) && l.kind === 'vacation').flatMap(l => leaveDates(l.startDate, l.endDate)).sort();
        for (const day of days) {
            const year = Number(day.slice(0, 4));
            if (day.slice(5) <= CARRY_UNTIL && of(year - 1) > 0) left[year - 1]--;
            else { of(year); left[year]--; }
        }
        return left;
    }
    /** Balance shown in the ficha for `today`: this year's days and, until 15 January, last year's. */
    function vacationBalance(person, leaves, today = new Date()) {
        const year = today.getFullYear(), left = vacationLeft(person, leaves), allowance = vacationAllowance(person);
        const carry = toIso(today).slice(5) <= CARRY_UNTIL ? Math.max(0, left[year - 1] ?? allowance) : 0;
        return { year, allowance, left: left[year] ?? allowance, carry, carryUntil: `${year}-${CARRY_UNTIL}` };
    }
    /** Approved unpaid-leave days falling in that month (monthIndex 0-11). */
    function unpaidDaysInMonth(leaves, year, monthIndex) {
        const prefix = `${year}-${pad(monthIndex + 1)}-`;
        return (leaves || []).filter(l => l.status === 'approved' && l.kind === 'unpaid')
            .reduce((total, l) => total + leaveDates(l.startDate, l.endDate).filter(d => d.startsWith(prefix)).length, 0);
    }

    /**
     * Hours a fixed-salary person works in a month (monthIndex 0-11) by their schedule: days under
     * contract, minus centre holidays and approved leave.
     * @param {any} person @param {number} year @param {number} monthIndex
     * @param {{ holidays?: string[], leaves?: { status: string, startDate: string, endDate: string }[] }} [options]
     */
    function workHoursInMonth(person, year, monthIndex, { holidays = [], leaves = [] } = {}) {
        const schedule = person?.workSchedule, length = new Date(year, monthIndex + 1, 0).getDate();
        if (!schedule?.regular) return 0;
        const off = new Set(holidays);
        const away = new Set((leaves || []).filter(l => l.status === 'approved').flatMap(l => leaveDates(l.startDate, l.endDate)));
        let hours = 0;
        for (let d = 1; d <= length; d++) {
            const date = new Date(year, monthIndex, d, 12), iso = toIso(date);
            if (!employedOn(person, iso) || off.has(iso) || away.has(iso)) continue;
            hours += dayHours((daysFor(schedule, date) || [])[(date.getDay() + 6) % 7]);
        }
        return Math.round(hours * 100) / 100;
    }

    const api = { DAY_LABELS, PAY_TYPES, normalizeSchedule, isSummer, daysFor, dayHours, weeklyHours, regularWeeklyHours,
        isFixed, hourlyRate, monthlySalary, employedInMonth, fixedCostInMonth,
        contractsOf, contractOn, focusContract, withFocus, isFreelance, freelanceInMonth, employedOn, contractDaysInMonth, rateOn, contractsProblem,
        LEAVE_KINDS, DEFAULT_VACATION_DAYS, CONTRACT_TYPES, hasVacations, validDate, leaveDates, vacationAllowance, vacationLeft, vacationBalance, unpaidDaysInMonth, workHoursInMonth };
    if (typeof module === 'object' && module.exports) module.exports = api;
    else root.StaffPay = api;
})(typeof window === 'undefined' ? globalThis : window);
